<template>
    <div class="assignment-table-wrap">
        <table style="width: 100%">
            <tr>
                <td>
                    <o-input v-model="filter" icon="filter" placeholder="Filter results" expanded clearable class="mb-4"></o-input>
                    <o-switch v-model="showHidden">Show hidden columns</o-switch>
                </td>
                <td class="has-text-right">
                    <div>{{ $filters.AddCommas(assignmentsFiltered.length) }} Assignments</div>
                    <div>
                        {{
                            $filters.Currency(
                                $store.getters.user.Currency,
                                assignmentsFiltered.reduce((acc, x) => (acc += x.totalBillings), 0)
                            )
                        }}
                        Total billings
                    </div>
                </td>
            </tr>
        </table>

        <o-table :data="assignmentsFiltered" :paginated="assignmentsFiltered.length > openColumns" :pagination-simple="true" :per-page="openColumns" :defaultSort="[sort, direction]" :sticky-header="true" hoverable>
            <template #empty>
                <h3 class="py-6">No assignments match the filters</h3>
            </template>
            <o-table-column field="Id" label="Job Title" numeric sortable v-slot="props" class="default">
                <router-link target="_blank" :to="{ name: '_assignmentDetail', params: { Id: props.row.Id } }">
                    {{ props.row.jobTitle }}
                </router-link>
            </o-table-column>
            <o-table-column field="company" label="Client" numeric sortable v-slot="props">
                <router-link target="_blank" :to="{ name: '_companyDetail', params: { Id: props.row.companyId } }">
                    {{ props.row.company }}
                    <span v-if="props.row.isKeyAccount"><i class="fal fa-key theme-key"></i></span>
                </router-link>
            </o-table-column>
            <o-table-column field="displayId" label="Assignment #" sortable v-slot="props" v-if="showHidden">
                <router-link target="_blank" :to="{ name: '_assignmentDetail', params: { Id: props.row.Id } }">
                    {{ props.row.displayId }}
                </router-link>
            </o-table-column>
            <o-table-column field="industry" label="Industries" v-slot="props" v-if="width > 74">
                {{ props.row.industry }}
            </o-table-column>
            <o-table-column field="searchType" label="Search Type" v-slot="props">
                <div v-if="props.row.searchType == 'BLM' || props.row.searchType == 'Consulting'">
                    {{ props.row.assignmentType }}
                </div>
                <div v-else>
                    {{ $filters.SearchType(props.row.searchType) }}
                </div>
            </o-table-column>
            <o-table-column field="users" label="Partners" v-slot="props">
                <div class="partners">
                    <router-link v-for="user in JSON.parse(props.row.users)" :to="{ name: '_partnerProfile', params: { Id: user.ID || user.Id } }" :key="user.ID">
                        {{ user.FullName }}
                    </router-link>
                </div>
            </o-table-column>
            <o-table-column field="users" label="Role" v-slot="props" style="white-space: nowrap" v-if="width > 58">
                <div v-for="user in JSON.parse(props.row.users)" :key="user.ID">
                    <span style="white-space: nowrap">{{ $filters.UserRole(user) }}</span>
                </div>
            </o-table-column>
            <o-table-column field="users" label="Location" v-slot="props" v-if="width > 66">
                <div v-for="user in JSON.parse(props.row.users)" :key="user.ID">
                    {{ user.LocationName }}
                </div>
            </o-table-column>
            <o-table-column field="status" label="Status" sortable v-slot="props">
                {{ $filters.AssignmentStatus(props.row.status) }}
            </o-table-column>
            <o-table-column field="dateTaken" label="Taken" sortable v-slot="props">
                {{ moment(props.row.dateTaken).format("MM/DD/YYYY") }}
            </o-table-column>
            <o-table-column field="totalBillings" position="right" :label="$store.getters.user.Currency" sortable v-slot="props" v-if="width > 42">
                {{ $filters.Currency($store.getters.user.Currency, props.row.totalBillings) }}
            </o-table-column>
        </o-table>
    </div>
</template>
<script>
import { ref, inject, watch, computed, onMounted, getCurrentInstance } from "vue";
import { useRouter } from "vue-router";

export default {
    props: {
        data: {
            type: Array,
            default: () => {
                return [];
            }
        },
        sort: {
            type: String,
            default: "dateTaken"
        },
        direction: {
            type: String,
            default: "desc"
        },
        width: {
            type: Number,
            default: null
        },
        perPage: {
            type: Number,
            default: 50
        },
        infiniteScroll: {
            type: Boolean,
            default: false
        }
    },
    setup(props, context) {
        const $router = useRouter();
        const moment = inject("$moment");

        const sort = ref(props.sort);
        const direction = ref(props.direction);

        const assignmentsLocal = computed(() => props.data);
        const filter = ref("");
        const showHidden = ref(false);

        const global = getCurrentInstance()?.appContext.config.globalProperties;

        const assignmentsFiltered = computed(() => {
            if (!filter.value || filter.value == "") return assignmentsLocal.value;

            const filterString = filter.value.toLowerCase();

            return assignmentsLocal.value.filter((x) => {
                if (
                    x.jobTitle?.toLowerCase().indexOf(filterString) >= 0 ||
                    x.company?.toLowerCase().indexOf(filterString) >= 0 ||
                    JSON.parse(x.users)?.some((u) => u.FullName.toLowerCase().indexOf(filterString) >= 0) ||
                    global.$filters.AssignmentStatus(x.status)?.toLowerCase().indexOf(filterString) >= 0 ||
                    x.Id.toString().includes(filterString) ||
                    x.displayId.toString().includes(filterString)
                )
                    return true;
            });
        });

        const consultingType = computed(() => {
            return assignmentsLocal.value.assignmentType.replace("Leadership Consulting - ", "");
        });

        const openColumns = ref(props.perPage);

        const RowClicked = (row, index) => {
            if (index == 0) return;
            const route = $router.resolve({ name: "_assignmentDetail", params: { Id: row.Id } });
            window.open(route.href);
        };

        if (props.infiniteScroll) {
            const main = document.getElementById("main");
            main.onscroll = function (event) {
                const element = event.target;

                if (element.scrollHeight - element.scrollTop <= element.clientHeight + 2) {
                    openColumns.value += props.perPage;
                }
            };
        }

        return {
            moment,
            filter,
            showHidden,
            assignmentsFiltered,
            openColumns,
            consultingType,
            RowClicked
        };
    }
};
</script>
<style lang="scss">
@import "@/assets/css/globalVars.scss";

.assignment-table-wrap {
    &>.b-table.is-mobile>.table-wrapper>.table>tbody>tr>td {
        &:first-child {
            border-bottom: none !important;
            padding-bottom: 0 !important;
            //background-color: transparent;
        }

        //To be revised
        &:nth-child(2) {
            border-top: none;
            border-bottom: 2px solid #cacaca !important;
            padding-top: 0;

            background-color: #f6f6f6;

            &::before {
                content: "";
                display: none;
            }

            & a {
                color: $text-gray;
                font-size: pxUnits(18);

                &:hover {
                    color: $boyden-blue;
                }
            }
        }
    }
}
</style>
<style lang="scss" scoped>
.partners {
    display: flex;
    flex-direction: column;
}

:deep() {}
</style>
